import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSlug } from "@/hooks/useSlug";
import { projectConnectionMutations } from "@/queries/project-connections/mutations";
import { useProjectConnectionQueries } from "@/queries/project-connections/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { JoinProjectConnection } from "./JoinProjectConnection";

export function JoinProjectConnectionPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t("page.project-connection.join.title"));
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const navigate = useNavigate();
  const projectConnectionQueries = useProjectConnectionQueries();
  const invitesQuery = useQuery({
    ...projectConnectionQueries.invites(),
    enabled: Boolean(sessionUser.onboardedAt) && sessionUser.newConnectionStatus !== "none",
  });
  const joinConnectionsMutation = projectConnectionMutations.useJoinProjectConnections();

  if (!sessionUser.onboardedAt) {
    return <Navigate to={routes.onboardingFlow.slides({ slug })} replace />;
  }

  if (sessionUser.newConnectionStatus === "none") {
    return <Navigate to={routes.communityFeed.list({ slug })} replace />;
  }

  if (invitesQuery.error) {
    return <ErrorPage error={invitesQuery.error} />;
  }

  if (invitesQuery.isPending) {
    return <FullSizeLoader withPadding />;
  }

  if (invitesQuery.data.length === 0) {
    return <ErrorPage />;
  }

  const handleContinue = () => {
    joinConnectionsMutation.mutate(
      invitesQuery.data.map((invite) => invite.id),
      {
        onSuccess: () => {
          void navigate(routes.communityFeed.list({ slug }), { replace: true });
        },
      },
    );
  };

  return (
    <JoinProjectConnection
      invites={invitesQuery.data}
      projectName={sessionUser.project.name}
      isJoining={joinConnectionsMutation.isPending}
      onContinue={handleContinue}
    />
  );
}
